import { FormField } from '../../../types/form-builder.types';
import { cn, useLanguage } from '@shondalai/ui-commons';
import { useState } from 'react';

interface NPSFieldProps {
  field: FormField;
  value: any;
  onChange: (value: any) => void;
  error?: string;
}

export function NpsField({ field, value, onChange, error }: NPSFieldProps) {
  const config = field.config;
  const { t } = useLanguage();
  const [followUpComment, setFollowUpComment] = useState('');
  const showFollowUp = config.showFollowUp && value !== null && value !== undefined;
  
  const npsValue = typeof value === 'object' ? value?.score : value;

  const handleScoreChange = (score: number) => {
    if (config.showFollowUp) {
      onChange({ score, comment: followUpComment });
    } else {
      onChange(score);
    }
  };

  const handleCommentChange = (comment: string) => {
    setFollowUpComment(comment);
    onChange({ score: npsValue, comment });
  };

  // NPS Category: 0-6 = Detractor, 7-8 = Passive, 9-10 = Promoter
  const getScoreCategory = (score: number) => {
    if (score <= 6) return 'detractor';
    if (score <= 8) return 'passive';
    return 'promoter';
  };

  return (
    <div className="space-y-3">
      {/* NPS Scale (0-10) */}
      <div className="flex justify-between gap-1">
        {Array.from({ length: 11 }, (_, index) => {
          const isSelected = npsValue === index;
          const category = getScoreCategory(index);

          return (
            <button
              key={index}
              type="button"
              onClick={() => handleScoreChange(index)}
              className={cn(
                "flex-1 h-10 md:h-11 rounded-md border transition-all duration-200",
                "hover:border-primary/60 hover:scale-105",
                "focus:outline-none focus:ring-2 focus:ring-primary/20 focus:ring-offset-1",
                !isSelected && "border-border/40 bg-background",
                error && !isSelected && "border-destructive/50",
                // Category-based colors when selected
                isSelected && category === 'detractor' && "bg-red-500 border-red-500 text-white font-medium shadow-sm scale-105",
                isSelected && category === 'passive' && "bg-amber-500 border-amber-500 text-white font-medium shadow-sm scale-105",
                isSelected && category === 'promoter' && "bg-emerald-500 border-emerald-500 text-white font-medium shadow-sm scale-105"
              )}
            >
              <span className="text-xs md:text-sm font-medium">{index}</span>
            </button>
          );
        })}
      </div>

      {/* Labels */}
      {config.showLabels !== false && (
        <div className="flex justify-between text-xs font-light px-1" style={{ color: 'var(--ef-textSecondary, #64748B)' }}>
          <span>{config.lowLabel || t('COM_EASYFORMS_NOT_LIKELY', 'Not at all likely')}</span>
          <span>{config.highLabel || t('COM_EASYFORMS_EXTREMELY_LIKELY', 'Extremely likely')}</span>
        </div>
      )}

      {/* Follow-up Comment */}
      {showFollowUp && (
        <div className="pt-3 space-y-1.5">
          <label className="easyforms-field-label">
            {t('COM_EASYFORMS_TELL_US_WHY', 'Please tell us why you gave this score')}
          </label>
          <textarea
            value={followUpComment}
            onChange={(e) => handleCommentChange(e.target.value)}
            placeholder={t('COM_EASYFORMS_YOUR_FEEDBACK', 'Your feedback helps us improve...')}
            rows={3}
            className="easyforms-field-textarea"
          />
        </div>
      )}
    </div>
  );
}
